// CORE
import { DateTime } from 'luxon';
import React, { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { loadState, useLoadState } from 'app-components/hooks/utils';
import { getLogger, tracker } from 'app-components/utils/core';
import { useAppSelector } from 'hooks/redux';

// MUI
import ArrowRightAltRoundedIcon from '@mui/icons-material/ArrowRightAltRounded';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import LockRoundedIcon from '@mui/icons-material/LockRounded';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import DialogContent from '@mui/material/DialogContent';
import Typography from '@mui/material/Typography';
import { makeStyles } from 'tss-react/mui';

// DATA
import { a2aTransfersActions, a2aTransfersSelectors, a2aTransfersUtils } from 'app-components/flux/a2aTransfers';
import type { accountsTypes } from 'app-components/flux/accounts';
import { accountsActions, accountsSelectors } from 'app-components/flux/accounts';
import { actionHelpers } from 'app-components/flux/core';
import { dateSelectors } from 'app-components/flux/date';
import { rootUiActions } from 'app-components/flux/root-ui';
import type { transactionsTypes } from 'app-components/flux/transactions';
import { transactionsActions } from 'app-components/flux/transactions';
import { numberUtils } from 'app-components/utils/number';
import * as fundingAccountsActions from 'data/fundingAccounts/actions';
import * as fundingAccountsSelectors from 'data/fundingAccounts/selectors';
import * as fundingAccountsUtils from 'data/fundingAccounts/utils';

import Button from '@mui/material/Button';
// COMPONENTS
import StdDialog from 'components/Dialogs/StdDialog';
import StdDialogActions from 'components/Dialogs/StdDialogActions';
import LoadingView from 'components/LoadingView';
import AmountField from 'components/QuickenControls/AmountField';
import ZeroStateView from 'components/ZeroStateView';
import TransferAccountSelect from 'containers/Settings/ChildPages/TransferHistory/TransferAccountSelect';

// LOCAL
import { type AccountTransfersDialogProps, showOnboardAccountsDialog } from 'components/Dialogs/A2A/actions';
import QIllustration from 'components/QIllustration';
import { type TransferDialogIndex, transferDialogIndexEnums } from './transferDialogIndexEnums';

const log = getLogger('components/Dialogs/A2A/AccountTransfersDialog');

export const DIALOG_TYPE_ACCOUNTS_TRANSFERS = 'DIALOG_TYPE_ACCOUNTS_TRANSFERS';

const useStyles = makeStyles()((theme) => ({
  readyWrapper: {
    display: 'flex',
    flexDirection: 'row',
    gap: theme.spacing(2),
    alignItems: 'flex-start',
  },
  errorTab: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    textAlign: 'center',
  },
  secondaryText: {
    width: '70%',
    marginTop: 10,
    textAlign: 'center',
    marginBottom: 30,
    color: theme.palette.text.secondary,
  },
  firstContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  headerText: {
    color: theme.palette.text.secondary,
  },
  inputField: {
    textAlign: 'left',
  },
  hiddenText: {
    opacity: 0.2,
  },
  text: {
    display: 'flex',
    flexDirection: 'row',
  },
}));

type TransferResponse = {
  config: any;
  data: {
    resources: transactionsTypes.TransactionProps[];
  };
  headers: any;
  request: any;
  status: number;
  statusText: string;
};

// When creating the dialog, there are two possibilities
// FromAccount and ToAccount will be valid. OR
// FromAccount will be invalid, and the only one selected.
// Due to this, we do not need to worry about handling the destination account, only the source account.
const handleInitialAccounts = (
  fromAccount: accountsTypes.Account | undefined,
  toAccount: accountsTypes.Account | undefined,
) => {
  const isFromInvalid = fundingAccountsUtils.isAccountA2aDepositOnly(fromAccount);
  if (isFromInvalid) {
    return { fromAccount: undefined, toAccount: fromAccount };
  }
  return { fromAccount, toAccount };
};

const formatArrivalDate = (date: string) => DateTime.fromISO(date).toFormat('MMM d');

type AccountCellProps = {
  label: string;
  account: accountsTypes.Account | undefined;
  headerClass: string;
  balance: number;
};

const AccountCell = ({ label, account, headerClass, balance }: AccountCellProps) => (
  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25, py: 0.5, minWidth: 0 }}>
    <Typography variant='paragraphRegular' className={headerClass}>
      {label}
    </Typography>
    <Typography variant='bodyMedium' noWrap>
      {account?.name}
    </Typography>
    {account && (
      <Typography variant='captionRegular' className={headerClass}>
        {`${numberUtils.formatNumber(balance, 'USD', '0,0.00')} available`}
      </Typography>
    )}
  </Box>
);

const AccountTransfersDialog = React.memo((props: AccountTransfersDialogProps) => {
  const isMounted = useRef<boolean>(undefined);
  const { dialogId, onClose, onCreation, startingIndex, transferOptions, disableBack, initiator } = props;
  const dispatch = useDispatch();
  const { classes } = useStyles();
  const navigate = useNavigate();

  //  ================ SELECTORS =================
  const today = useAppSelector(dateSelectors.getCurrentDateString);
  const hasTransfers = useAppSelector(a2aTransfersSelectors.getHasTransfers);
  const fundingAccountsByAccountId = useAppSelector(fundingAccountsSelectors.getVerifiedFundingAccountsByAccountId);
  const trueStartIndex =
    startingIndex || (!hasTransfers ? transferDialogIndexEnums.OPENING : transferDialogIndexEnums.SELECT_ACCTS);

  // ================= STATES ================
  const [fromAccount, setFromAccount] = useState(
    handleInitialAccounts(transferOptions?.fromAccount, transferOptions?.toAccount).fromAccount,
  );
  const [toAccount, setToAccount] = useState(
    handleInitialAccounts(transferOptions?.fromAccount, transferOptions?.toAccount).toAccount,
  );
  const [minDate, setMinDate] = useState('');
  const [transferAmount, setTransferAmount] = useState(transferOptions?.transferAmount || 0);
  const [disableContinue, setDisableContinue] = useState(true);
  const [errorCode, setErrorCode] = useState<string | undefined>();
  const [errorMessage, setErrorMessage] = useState('Please try again at another time.');
  const [timerCount, setTimerCount] = useState(0);
  const [isTimerRunning, setIsTimerRunning] = useState(false);
  const [minMaxValuesGetSuccess, setMinMaxValuesGetSuccess] = useState(true);
  const [forceLoading, setForceLoading] = useState(trueStartIndex === transferDialogIndexEnums.SUBMIT);

  // ================== TAB INDEX =======================
  const [tabIndex, setTabIndex] = useState<TransferDialogIndex>(trueStartIndex);

  const decTabIndex = () => {
    switch (tabIndex) {
      case transferDialogIndexEnums.SELECT_ACCTS:
        setTabIndex(transferDialogIndexEnums.OPENING);
        break;
      case transferDialogIndexEnums.SUBMIT:
        setMinDate('');
        setTabIndex(transferDialogIndexEnums.SELECT_ACCTS);
        break;
      default:
    }
  };

  const incTabIndex = () => {
    switch (tabIndex) {
      case transferDialogIndexEnums.OPENING:
        setTabIndex(transferDialogIndexEnums.SELECT_ACCTS);
        break;
      case transferDialogIndexEnums.SELECT_ACCTS:
        setTabIndex(transferDialogIndexEnums.SUBMIT);
        break;
      default:
    }
  };

  //  ================= VIEW STATE LOGIC =====================
  // Views
  const view = useLoadState(fundingAccountsSelectors);
  const accountsView = useLoadState(accountsSelectors);
  //  No view of the transfer history selectors because they are not crucial to the functionality of the dialog and only effect cosmetic changes.
  const mainView = useMemo(() => {
    const tooFewAccounts = fundingAccountsByAccountId?.size < 2;

    const showTransferUnavailableView = tabIndex === transferDialogIndexEnums.TRANSFER_UNAVAILABLE;
    const showErrorView = tabIndex === transferDialogIndexEnums.ERROR;
    const showSkeletonViewWithText = tabIndex === transferDialogIndexEnums.LOADING;
    const showSkeletonView =
      (loadState.skeletonStates.includes(view) || loadState.skeletonStates.includes(accountsView)) &&
      tabIndex !== transferDialogIndexEnums.LOADING;
    const showTryAgainView =
      (loadState.tryAgainStates.includes(view) ||
        loadState.tryAgainStates.includes(accountsView) ||
        loadState.updateRequiredStates.includes(view) ||
        loadState.updateRequiredStates.includes(accountsView)) &&
      !tooFewAccounts &&
      tabIndex !== transferDialogIndexEnums.LOADING;
    const showNoDataView =
      (loadState.noDataStates.includes(view) || loadState.noDataStates.includes(accountsView) || tooFewAccounts) &&
      tabIndex !== transferDialogIndexEnums.LOADING;
    const showReadyView =
      loadState.readyStates.includes(view) &&
      loadState.readyStates.includes(accountsView) &&
      !tooFewAccounts &&
      tabIndex !== transferDialogIndexEnums.LOADING;

    if (showTransferUnavailableView) {
      return 'TRANSFER_UNAVAILABLE';
    } else if (showErrorView) {
      return 'ERROR';
    } else if (showSkeletonViewWithText) {
      return 'LOADING_TEXT';
    } else if (showReadyView) {
      return 'READY';
    } else if (showNoDataView) {
      return 'EMPTY';
    } else if (showTryAgainView) {
      return 'FAILED';
    } else if (showSkeletonView) {
      return 'LOADING';
    }
    return 'EMPTY';
  }, [accountsView, fundingAccountsByAccountId?.size, tabIndex, view]);

  // =============== ONMOUNT EFFECT =================
  useEffect(() => {
    tracker.track(tracker.events.transferStart, { initiator });
    if (onCreation) {
      onCreation();
    }
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const myTimer = setInterval(() => {
      if (isTimerRunning) {
        setTimerCount(timerCount + 1);
      }
    }, 750);
    // clearing interval
    return () => clearInterval(myTimer);
  });

  useEffect(() => {
    if (tabIndex === transferDialogIndexEnums.SUBMIT) {
      const userTimeZone = DateTime.now().zoneName;
      const payload = {
        maxDays: 365,
        zoneId: userTimeZone,
        calendarStartDate: today,
        amount: numberUtils.formatNumber(transferAmount, null, '0.00') || '1.00',
      };
      setForceLoading(true);
      actionHelpers
        .bindPromiseAction(
          dispatch,
          a2aTransfersActions.getA2aTransferInfo,
        )(payload)
        .then((resp: any) => {
          setMinDate(resp?.data?.minArriveOn);
          setMinMaxValuesGetSuccess(resp?.data?.minArriveOn); //success? True or false
          setTimeout(() => {
            setForceLoading(false);
          }, 300); // Slightly increase loading time so it isn't jittery when loading in
        })
        .catch((error) => {
          if (a2aTransfersUtils.isTransferUnavailableError(error)) {
            setTabIndex(transferDialogIndexEnums.TRANSFER_UNAVAILABLE);
          }
          setMinDate('');
          setMinMaxValuesGetSuccess(false); //success? True or false
          setTimeout(() => {
            setForceLoading(false);
          }, 300);
        });
    }
  }, [dispatch, tabIndex, today]); // eslint-disable-line react-hooks/exhaustive-deps

  // ================ CALLBACKS ================

  // TODO: Add this to app-components
  const getAccountBalance = useCallback((account: accountsTypes.Account) => {
    const aggregation = account?.aggregators?.first?.();
    const availableBalance = aggregation?.availableBalance;
    const onlineBalance = aggregation?.onlineBalance;
    const balance = typeof availableBalance === 'number' ? availableBalance : Number(availableBalance ?? onlineBalance);

    return Number.isFinite(balance) ? balance : 0;
  }, []);

  const refreshData = useCallback(() => {
    dispatch(fundingAccountsActions.getFundingAccounts(undefined));
    dispatch(accountsActions.getAccounts(undefined));
  }, [dispatch]);

  const handleTransferUnavailable = useCallback(() => {
    setTabIndex(transferDialogIndexEnums.TRANSFER_UNAVAILABLE);
  }, []);

  const handleClose = () => {
    dispatch(rootUiActions.removeDialog(dialogId));
    const isError = tabIndex === transferDialogIndexEnums.ERROR;
    onClose?.(isError);
  };

  const handleNavigateTransferActivity = () => {
    navigate(`/settings/transfers`);
    handleClose();
  };

  const handleOpenSetup = () => {
    handleClose();
    dispatch(
      showOnboardAccountsDialog({
        initiator: 'transfer_funds_dialog',
      }),
    );
  };

  const navigateToAccountSettings = () => {
    handleClose();
    navigate(`/settings/accounts`);
  };

  const handleInitiateTransfer = async () => {
    if (fromAccount?.id && toAccount?.id && transferAmount) {
      setIsTimerRunning(true);
      setTabIndex(transferDialogIndexEnums.LOADING);
      const payload = {
        srcAccountId: fromAccount.id,
        dstAccountId: toAccount.id,
        amount: String(transferAmount),
      };

      actionHelpers
        .bindPromiseAction<transactionsTypes.A2ATransferPayload, any, TransferResponse>(
          dispatch,
          transactionsActions.initiateA2ATransfer,
        )(payload)
        .then((response) => {
          setIsTimerRunning(false);
          if (response?.status === 200) {
            tracker.track(tracker.events.transferSubmit, {
              speed: DateTime.fromISO(response?.data?.resources?.[0]?.postedOn).diff(
                DateTime.now().startOf('day'),
                'days',
              )?.days,
              amount: transferAmount,
              from_type: fromAccount?.subType ?? 'ERROR',
              to_type: toAccount?.subType ?? 'ERROR',
            });
            if (isMounted.current === true) {
              setTabIndex(transferDialogIndexEnums.SUCCESS);
            }
          } else {
            tracker.track(tracker.events.transfersError);
            if (isMounted.current === true) {
              setErrorCode('');
              setErrorMessage('Please try again another time.');
              setTabIndex(transferDialogIndexEnums.ERROR);
            }
          }
        })
        .catch((error) => {
          setIsTimerRunning(false);

          if (isMounted.current === true) {
            if (a2aTransfersUtils.isTransferUnavailableError(error)) {
              setTabIndex(transferDialogIndexEnums.TRANSFER_UNAVAILABLE);
              return;
            }
            const firstError = error.response?.data?.errors?.[0];
            setErrorCode(error?.response?.status);
            setErrorMessage(
              firstError?.detail ||
                firstError?.title ||
                error.response?.error ||
                'We ran into a problem initiating your transfer, try again another time',
            );
            setTabIndex(transferDialogIndexEnums.ERROR);
          }
        });
    } else {
      log.warn('Transfer initiation attempted without necessary info!');
    }
  };

  const handleContinue = () => {
    if (tabIndex === transferDialogIndexEnums.SELECT_ACCTS || tabIndex === transferDialogIndexEnums.OPENING) {
      incTabIndex();
    } else if (tabIndex === transferDialogIndexEnums.SUBMIT) {
      // initiate transfers
      handleInitiateTransfer();
    } else if (tabIndex === transferDialogIndexEnums.SUCCESS) {
      handleClose();
    }
  };

  // ============== DERIVED VALUES ===============
  let dialogTitle = 'Transfer funds';
  if (tabIndex === transferDialogIndexEnums.ERROR) dialogTitle = 'Transfer failed';
  if (tabIndex === transferDialogIndexEnums.SUBMIT) dialogTitle = 'Review transfer';
  if (tabIndex === transferDialogIndexEnums.SUCCESS) dialogTitle = 'Transfer sent';
  if (tabIndex === transferDialogIndexEnums.LOADING) dialogTitle = 'Sending transfer';

  let continueLabel = 'Continue';
  if (tabIndex === transferDialogIndexEnums.OPENING) {
    continueLabel = 'Got it';
  } else if (tabIndex === transferDialogIndexEnums.SELECT_ACCTS) {
    continueLabel = 'Review transfer';
  } else if (tabIndex === transferDialogIndexEnums.SUBMIT) {
    continueLabel = 'Confirm transfer';
  } else if (tabIndex === transferDialogIndexEnums.SUCCESS) {
    continueLabel = 'Done';
  }

  return (
    <StdDialog
      dialogId={dialogId}
      disableBackdropClick={tabIndex === transferDialogIndexEnums.LOADING}
      onBack={decTabIndex}
      onClose={handleClose}
      open
      sharedcomponentid='DIALOG_TYPE_ACCOUNTS_TRANSFERS'
      showBackButton={
        !disableBack &&
        (tabIndex === transferDialogIndexEnums.SUBMIT ||
          (tabIndex === transferDialogIndexEnums.SELECT_ACCTS && !hasTransfers))
      }
      showCloseButton={tabIndex !== transferDialogIndexEnums.LOADING}
      size='md'
      title={dialogTitle}
      paperProps={{ sx: { width: 768, maxWidth: 768 } }}
    >
      <DialogContent sx={{ pt: 3, px: 3, pb: 6 }}>
        {tabIndex === transferDialogIndexEnums.OPENING && mainView === 'READY' && (
          <div className={classes.firstContainer}>
            <QIllustration name='transferToAccount' width={360} height={168} />
            <Typography variant='displayRegular'>Transfer money between accounts</Typography>
            <Typography className={classes.secondaryText} variant='bodyRegular'>
              You can now easily move money right here in Simplifi. Transfers usually complete within a couple of
              business days.
            </Typography>
          </div>
        )}
        {tabIndex === transferDialogIndexEnums.SELECT_ACCTS && mainView === 'READY' && (
          <TransferAccountSelect
            fromAccount={fromAccount}
            setFromAccount={setFromAccount}
            toAccount={toAccount}
            setToAccount={setToAccount}
            transferAmount={transferAmount}
            setTransferAmount={setTransferAmount}
            setDisableContinue={setDisableContinue}
            onOptionsFetch={setMinMaxValuesGetSuccess}
            onTransferUnavailable={handleTransferUnavailable}
          />
        )}
        {tabIndex === transferDialogIndexEnums.SUBMIT && mainView === 'READY' && (
          <div className={classes.readyWrapper}>
            <Box sx={{ flexShrink: 0 }}>
              <QIllustration name='transfers' size='lg' />
            </Box>
            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 4, minWidth: 0 }}>
              <Typography variant='bodyRegular' color='text.primary'>
                Double check your transfer details before submitting.
              </Typography>
              {/* CSS grid: 2 rows × 3 cols — guarantees arrow and "Estimated arrival" always
                  align with "To account" regardless of content length in either row */}
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: '165px min-content 1fr',
                  columnGap: 3,
                  rowGap: 3,
                }}
              >
                <AccountCell
                  label='Transfer from'
                  account={fromAccount}
                  headerClass={classes.headerText}
                  balance={fromAccount ? getAccountBalance(fromAccount) : 0}
                />
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <ArrowRightAltRoundedIcon sx={{ color: 'icon.light' }} />
                </Box>
                <AccountCell
                  label='To account'
                  account={toAccount}
                  headerClass={classes.headerText}
                  balance={toAccount ? getAccountBalance(toAccount) : 0}
                />
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25, py: 0.5 }}>
                  <Typography variant='paragraphRegular' className={classes.headerText}>
                    Transfer amount
                  </Typography>
                  <AmountField className={classes.inputField} variant='bodyMedium' value={transferAmount} />
                </Box>
                {/* invisible arrow placeholder keeps column widths consistent */}
                <Box sx={{ visibility: 'hidden', display: 'flex', alignItems: 'center' }}>
                  <ArrowRightAltRoundedIcon />
                </Box>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25, py: 0.5 }}>
                  <Typography variant='paragraphRegular' className={classes.headerText}>
                    Estimated arrival
                  </Typography>
                  {forceLoading ? (
                    <Box mt={0.5}>
                      <LoadingView size={20} />
                    </Box>
                  ) : (
                    <Typography variant='bodyMedium'>{minDate ? formatArrivalDate(minDate) : ''}</Typography>
                  )}
                </Box>
              </Box>
              <Alert
                severity='info'
                icon={<InfoOutlinedIcon sx={{ fontSize: '16px' }} />}
                sx={{
                  borderRadius: '12px',
                  width: 471,
                  padding: '8px 16px',
                  backgroundColor: 'info.light',
                  '& .MuiAlert-icon': { padding: 0, mr: 1, alignItems: 'center', color: 'info.dark' },
                  '& .MuiAlert-message': { padding: 0 },
                }}
              >
                <Typography variant='captionRegular' sx={{ color: 'info.dark' }}>
                  You can cancel this transfer for a brief period after submitting.
                </Typography>
              </Alert>
            </Box>
          </div>
        )}
        {tabIndex === transferDialogIndexEnums.SUCCESS && mainView === 'READY' && (
          <Box
            textAlign='center'
            pl={18}
            pr={18}
            pt={8}
            pb={8}
            display='flex'
            flexDirection='column'
            alignItems='center'
            justifyContent='center'
          >
            <QIllustration name='thumbsUp' size='lg' />
            <br />
            <Typography variant='headlineRegular'>Your transfer is confirmed!</Typography>
            <br />
            {minDate ? (
              <span className={classes.text}>
                <Typography variant='paragraphRegular' color='textSecondary'>
                  {'You should see your transfer completed by'}&nbsp;
                </Typography>
                <Typography variant='paragraphBold' color='textSecondary'>
                  {formatArrivalDate(minDate)}.
                </Typography>
              </span>
            ) : (
              <Typography color='textSecondary' variant='paragraphRegular'>
                Transfers usually complete within a couple of business days.
              </Typography>
            )}
            <Typography color='textSecondary' variant='paragraphRegular'>
              {'You can track this transfer in Settings > Transfer Activity.'}
            </Typography>
          </Box>
        )}
        {mainView === 'TRANSFER_UNAVAILABLE' && (
          <Box className={classes.errorTab}>
            <ZeroStateView
              id='transfer-unavailable'
              title='Transfers are temporarily unavailable'
              description="We're updating our transfer service, so you can't schedule new transfers right now. Any transfers you've already scheduled will be processed as planned. Thanks for your patience - please check back soon."
              qIllustration='constructionLoan'
            />
          </Box>
        )}
        {mainView === 'ERROR' && (
          <Box className={classes.errorTab}>
            <ZeroStateView
              id='microdeposit-failure'
              title='Unable to process transfer'
              description={errorMessage}
              subDescription={errorCode && `Error Code: ${errorCode}`}
              qIllustration='planeCrash'
            />
          </Box>
        )}
        {mainView === 'LOADING' && (
          <Box
            paddingTop={20}
            paddingBottom={20}
            display='flex'
            alignItems='center'
            flexDirection='column'
            justifyContent='center'
          >
            <LoadingView size={100} />
          </Box>
        )}
        {mainView === 'LOADING_TEXT' && (
          <Box
            paddingTop={20}
            paddingBottom={20}
            display='flex'
            alignItems='center'
            flexDirection='column'
            justifyContent='center'
          >
            <LoadingView size={100} />
            <Typography variant='paragraphRegular' className={timerCount !== 0 ? classes.hiddenText : undefined}>
              {' '}
              Verifying your accounts{' '}
            </Typography>
            <Typography variant='paragraphRegular' className={timerCount !== 1 ? classes.hiddenText : undefined}>
              {' '}
              Initiating transfer{' '}
            </Typography>
            <Typography variant='paragraphRegular' className={timerCount < 2 ? classes.hiddenText : undefined}>
              {' '}
              Finalizing...{' '}
            </Typography>
          </Box>
        )}
        {mainView === 'EMPTY' && (
          <Box className={classes.errorTab}>
            <ZeroStateView
              id='zero-state'
              title='Not enough accounts enabled'
              description='To transfer money, you’ll need to set up at least one more account for transfers. Visit Account Settings to see the eligibility of all your accounts.'
              qIllustration='FInoneConnected'
            />
          </Box>
        )}
        {mainView === 'FAILED' && (
          <Box className={classes.errorTab}>
            <ZeroStateView
              button={
                <Button id='try-again-button' variant='outlined' onClick={refreshData}>
                  Try Again
                </Button>
              }
              id='failed-to-fetch'
              title='Oops! Something went wrong'
              description='We hit a snag and were unable to load account transfers.'
              qIllustration='planeCrash'
            />
          </Box>
        )}
      </DialogContent>
      {mainView === 'READY' && minMaxValuesGetSuccess && (
        <StdDialogActions
          primary={{
            disabled: (tabIndex === transferDialogIndexEnums.SELECT_ACCTS && disableContinue) || forceLoading,
            children: continueLabel,
            onClick: handleContinue,
            variant: 'contained',
          }}
          secondary={
            tabIndex === transferDialogIndexEnums.SUCCESS && initiator !== 'transfer activity widget'
              ? {
                  children: 'Transfer Activity',
                  onClick: handleNavigateTransferActivity,
                  variant: 'outlined',
                }
              : undefined
          }
          tertiaryNode={
            tabIndex === transferDialogIndexEnums.SELECT_ACCTS || tabIndex === transferDialogIndexEnums.SUBMIT ? (
              <Typography
                variant='captionRegular'
                sx={{ display: 'flex', alignItems: 'center', color: 'text.secondary' }}
              >
                <LockRoundedIcon sx={{ height: 16, width: 16, mr: 1, color: 'icon.light' }} />
                Safe and secure with Quicken
              </Typography>
            ) : undefined
          }
        />
      )}
      {mainView === 'EMPTY' && (
        <StdDialogActions
          primary={{
            children: 'Set Up Another Account',
            onClick: handleOpenSetup,
            variant: 'contained',
          }}
          secondary={{
            children: 'Account Settings',
            onClick: navigateToAccountSettings,
            variant: 'text',
          }}
        />
      )}
      {mainView === 'TRANSFER_UNAVAILABLE' && (
        <StdDialogActions
          primary={{
            children: 'Got it',
            onClick: handleClose,
            variant: 'contained',
          }}
        />
      )}
      {mainView === 'ERROR' && (
        <StdDialogActions
          primary={{
            children: 'close',
            onClick: handleClose,
            variant: 'outlined',
          }}
        />
      )}
    </StdDialog>
  );
});

AccountTransfersDialog.displayName = 'AccountTransfersDialog';

export default AccountTransfersDialog;
